import { useContext, useEffect, useState, useRef } from "react";
import GenericTable from "../../../Component/BuyingJourneyDataTable/GenericTable";
import { DiscoveryContext } from "../discovery-context";
import {
    transformFunnelData,
} from "../../../utils/helpers";
import classes from "./ContentMapping.module.css";
import { Chip } from "@mui/material";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import { filterKey } from "../BrandAudit";
import MUIIconButton from "./../../../Component/UI/MUIIconButton/MUIIconButton";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import Modal from "../../../Component/Modal/Modal";
import Share from "../Share/Share";
import FocusAreas from "../../MetricContentV2/FocusAreas/FocusAreas";
import BuyingJourneyDataTable from "../../../Component/BuyingJourneyDataTable/DataTable";
import {
    getNonEmptyKeys,
    transformFocusArea,
} from "../../../utils/metric-content-helpers";
import api from "../../../BrandOnBoarding/api";
import { ToastContainer, toast } from "react-toastify";
import MetricContentPDFExport from "../../MetricContentV2/PDFExport/MetricContentPDFExport";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import { AuthContext } from "../../../context/auth-context";

const ContentMapping = () => {
    const [focusAreaInputs, setFocusAreaInputs] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [contentMapData, setContentMapData] = useState([]);
    const [contentMapLoading, setContentMapLoading] = useState(false);
    const [selectedContentMapLlmItems, setSelectedContentMapLlmItems] = useState(
        []
    );
    const [contentMapLlm, setContentMapLlm] = useState();
    const discoveryCtx = useContext(DiscoveryContext);
    const { user } = useContext(AuthContext);
    const primaryLLM = discoveryCtx.primary_llm;
    const primaryPersona = discoveryCtx.persona[0];
    const primaryMarket = discoveryCtx.market;
    const market = discoveryCtx.markets;
    const selectedCategory = discoveryCtx.state.selectedCategory;
    // const conversationsData = discoveryCtx.conversations?.[primaryLLM]?.[selectedCategory]?.[primaryMarket]?.[primaryPersona?.title]?.data;
    const conversationsData = (() => {
        const personaConversations = discoveryCtx.conversations?.[primaryLLM]?.[selectedCategory]?.[primaryMarket]?.[primaryPersona?.title];
        if (!personaConversations || Object.keys(personaConversations).length === 0) return undefined;

        const latest = Object.values(personaConversations).reduce((prev, curr) => {
            if (!prev) return curr;
            return new Date(curr?.created_at) > new Date(prev?.created_at) ? curr : prev;
        }, null);

        return latest?.data;
    })();
    const contentRecord = discoveryCtx.contentMapping[primaryLLM][selectedCategory];
    const discoveryType = discoveryCtx.type;
    const transactionId = discoveryCtx.state.transactionId;
    const entity = discoveryCtx.entity;
    const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;
    const isInitialLoad = useRef(true);
    const MAX_LAUNCHES = discoveryCtx.auditLimits?.content_mapping_limits || 5;
    const remainingLaunches = discoveryCtx.auditLimits?.content_mapping_limits_remaining ?? MAX_LAUNCHES;
    // Helper function to detect record format
    const isOldFormat = (record) => {
        return record?.data &&
            !Array.isArray(record.data) &&
            (record.data.sentiment || record.data.visibility || record.data.brandPillars);
    };

    // Helper function to transform old format to new format structure
    const transformOldFormatToNew = (oldRecord) => {
        if (!oldRecord?.data) return null;

        // Extract focus area inputs from old format
        const focusInputs = {
            sentiment: oldRecord.data.sentiment || [],
            visibility: oldRecord.data.visibility || [],
            brandPillars: oldRecord.data.brandPillars || [],
            purchaseCriteria: oldRecord.data.purchaseCriteria || [],
            negativeAttributes: oldRecord.data.negativeAttributes || [],
            positiveAttributes: oldRecord.data.positiveAttributes || [],
            negativeSentimentDrivers: oldRecord.data.negativeSentimentDrivers || [],
            positiveSentimentDrivers: oldRecord.data.positiveSentimentDrivers || [],
        };

        // Extract content suggestions from old format
        const contentSuggestions = oldRecord.data.contentSuggestions || {};

        return {
            focusAreaInputs: focusInputs,
            contentSuggestions: contentSuggestions,
        };
    };
    // useEffect(() => {
    //     if (isInitialLoad.current && contentRecord) {
    //         // Check if it's old format or new format
    //         if (isOldFormat(contentRecord)) {
    //             // Handle old format
    //             const transformedData = transformOldFormatToNew(contentRecord);

    //             if (transformedData) {
    //                 setFocusAreaInputs(transformedData.focusAreaInputs);
    //                 setContentMapData(transformedData.contentSuggestions || []);
    //             }
    //         } else if (contentRecord?.data?.length > 0) {
    //             // Handle new format (array of records)
    //             const { contentSuggestions, focusAreaInputs } = contentRecord.data[0];
    //             setFocusAreaInputs(focusAreaInputs);
    //             setContentMapData(contentSuggestions || []);
    //         }

    //         isInitialLoad.current = false;
    //     }
    // }, [contentRecord]);

    useEffect(() => {
        if (contentRecord) {
            // Check if it's old format or new format
            if (isOldFormat(contentRecord)) {
                // Handle old format
                const transformedData = transformOldFormatToNew(contentRecord);

                if (transformedData) {
                    setFocusAreaInputs(transformedData.focusAreaInputs);
                    setContentMapData(transformedData.contentSuggestions || []);
                }
            } else if (contentRecord?.data?.length > 0) {
                // Handle new format (array of records)
                const firstRecord = contentRecord?.data?.at(0) ?? {};
                const { contentSuggestions = {}, focusAreaInputs = {} } = firstRecord;
                setFocusAreaInputs(focusAreaInputs);
                setContentMapData(contentSuggestions || []);
            }
        }
    }, [contentRecord, selectedCategory]); // ✅ Added selectedCategory dependency and removed isInitialLoad guard
    const handleContentMapLlmCheckChange = (selected) => {
        const selectedValues = selected?.map((option) => option?.name) || [];
        const selectedLlms = selected?.map((option) => option?.value) || [];
        setSelectedContentMapLlmItems(
            selectedValues.at(-1) ? [selectedValues.at(-1)] : []
        );
        setContentMapLlm(selectedLlms.at(-1));
    };

    const handleContentMapping = async () => {
        try {
            // Validate focus area inputs
            const data = transformFocusArea(focusAreaInputs, discoveryType, true);
            const selectedFocusAreaInputs = getNonEmptyKeys(data);
            if(remainingLaunches <= 0) return toast.error(`launch limit exhausted.${MAX_LAUNCHES} Maximum limits allowed`)
            discoveryCtx.updateLaunch({
                content_mapping_limits_remaining: remainingLaunches - 1
            });

            if (!selectedFocusAreaInputs || selectedFocusAreaInputs.length === 0) {
                toast.warn("No focus area selected!");
                return;
            }

            if (!conversationsData) {
                toast.warn("No discussion topics data available!");
                return;
            }

            // Validate required data
            if (!entity || !selectedCategory || !transactionId || !contentRecord?.request_id) {
                toast.error("Missing required information. Please try again.");
                return;
            }

            // Build luminaire_inputs
            const luminaire_inputs = {};
            for (const [key, value] of Object.entries(data)) {
                if (Array.isArray(value) && value.length > 0) {
                    luminaire_inputs[key] = value;
                }
            }

            if (Object.keys(luminaire_inputs).length === 0) {
                toast.warn("No valid inputs to process!");
                return;
            }

            // Build prompt results
            const promptResults = {};
            const requiredKeys = ["Reach", "Purchase_Criteria"];
            const requiredBrandMetricKeys = [
                "Negative_Attributes",
                "Positive_Attributes",
                "Brand_Pillars",
                "Product_Dimensions",
                "Negative_Sentiment_Drivers",
                "Positive_Sentiment_Drivers",
            ];

            const isCategoryData = requiredKeys.some((str) =>
                selectedFocusAreaInputs.includes(str)
            );
            const isBrandData = requiredBrandMetricKeys.some((str) =>
                selectedFocusAreaInputs.includes(str)
            );

            if (isCategoryData) {
                promptResults.category_discussion_topics =
                    conversationsData?.discussionTopics?.discussion_topics;
                promptResults.category_purchase_barriers =
                    conversationsData?.purchaseBarriers?.category_data;
                promptResults.category_purchase_enablers =
                    conversationsData?.purchaseEnablers?.category_data;
                promptResults.category_purchase_jounrey_questions =
                    conversationsData?.purchaseJourneyQuestions?.category_questions?.questions_by_funnel_stage;
            }

            if (isBrandData) {
                promptResults.brand_discussion_topics =
                    conversationsData?.discussionTopics?.entity_discussion_topics;
                promptResults.brand_purchase_barriers =
                    conversationsData?.purchaseBarriers?.entity_data;
                promptResults.brand_purchase_enablers =
                    conversationsData?.purchaseEnablers?.entity_data;
                promptResults.brand_purchase_jounrey_questions =
                    conversationsData?.purchaseJourneyQuestions?.entity_questions?.questions_by_funnel_stage;
            }

            // Prepare API payload
            const payload = {
                entity: entity,
                type: discoveryType === "product" ? "product" : "brand",
                category: selectedCategory,
                location: market?.[0] || "",
                persona: primaryPersona?.description,
                llm: "Claude3",
                luminaire_inputs,
                promptResults,
            };

            setContentMapLoading(true);

            // Generate content suggestions
            const response = await api.post(
                "/metrics/generate-metric-content",
                payload
            );

            // Validate API response
            if (!response?.data || Object.keys(response.data).length === 0) {
                toast.warn("No content suggestions generated. Please try again.");
                setContentMapLoading(false);
                return;
            }

            // Transform response data
            const transformedResponse = {
                ...response?.data,
                data: response?.data?.data?.map(item => ({
                    ...item,
                    type: item.type.replace("Reach", ENTITY_LABELS.rank),
                    theme: item.theme.replace("Reach", ENTITY_LABELS.rank)
                })) || [],
                filterKeys: response?.data?.filterKeys?.map(key => ({
                    ...key,
                    name: key.name.replace("Reach", ENTITY_LABELS.rank),
                    value: key.value.replace("Reach", ENTITY_LABELS.rank)
                })) || []
            };

            setContentMapData(transformedResponse);

            // Create new content mapping entry
            const contentMappingEntry = {
                focusAreaInputs: focusAreaInputs,
                contentSuggestions: transformedResponse,
                created_at: new Date()
            };

            // Prepare patch payload
            const patchPayload = {
                content_mapping: contentMappingEntry,
                transaction_id: transactionId,
                request_id: contentRecord.request_id,
                user_id: user?.user_role?.user_id,
            };

            // Update content mapping in backend
            const contentMapSuggestionsRecord = await api.patch(
                `${webSocketBaseURL}/content-mapping`,
                patchPayload
            );

            // Verify backend update was successful
            if (!contentMapSuggestionsRecord?.data?.success) {
                throw new Error("Failed to save content mapping");
            }
            const { data: responseData, content_mapping_limits_remaining } = contentMapSuggestionsRecord.data;
            if (content_mapping_limits_remaining !== undefined) {
                discoveryCtx.updateLaunch({
                    content_mapping_limits_remaining
                });
            }
            // Update context
            discoveryCtx.updateContentMapping({
                data: responseData,
                category: selectedCategory,
                llm: primaryLLM,
                request_id: contentRecord.request_id
            });

            setContentMapLoading(false);
            toast.success("Content Suggestions added successfully!");

        } catch (err) {
            console.error("Error in handleContentMapping:", err);
            setContentMapLoading(false);

            // Handle specific error types
            if (err.response) {
                // API error with response
                const errorMessage = err.response?.data?.message ||
                    err.response?.data?.error ||
                    "Failed to update content mapping";
                toast.error(errorMessage);
            } else if (err.request) {
                // Network error
                toast.error("Network error. Please check your connection and try again.");
            } else {
                // Other errors
                toast.error(err.message || "An unexpected error occurred");
            }
        }
    };

    // Prepare PDF export props with configuration and data
    const preparePDFExportProps = () => {
        // Prepare focus areas data - filter only selected items
        const focusAreasForPdf = {};
        if (focusAreaInputs) {
            Object.entries(focusAreaInputs).forEach(([key, items]) => {
                if (Array.isArray(items)) {
                    // Filter items that have selected=true or have text/name properties
                    const selectedItems = items.filter(
                        (item) => item?.selected || item?.text || item?.name
                    );
                    if (selectedItems.length > 0) {
                        focusAreasForPdf[key] = selectedItems;
                    }
                }
            });
        }

        // Prepare table data - convert to array of objects if needed
        const tableDataForPdf = contentMapData?.data || [];

        // Extract filter keys properly - handle both string array and object array
        let filterKeysForPdf = [];
        if (contentMapData?.filterKeys) {
            if (Array.isArray(contentMapData.filterKeys)) {
                filterKeysForPdf = contentMapData.filterKeys.map((key) => {
                    if (typeof key === 'string') {
                        return key;
                    } else if (typeof key === 'object' && key?.name) {
                        return key.name;
                    }
                    return String(key);
                });
            }
        }

        return {
            entityName: entity,
            category: selectedCategory,
            persona: primaryPersona?.description || primaryPersona?.title,
            market: primaryMarket || "",
            llm: primaryLLM || "",
            focusAreas: focusAreasForPdf,
            tableData: tableDataForPdf,
            filterKeys: filterKeysForPdf,
            isDisabled: contentMapLoading,
        };
    };
    // Determine historyData based on record format
    const getHistoryData = () => {
        if (isOldFormat(contentRecord)) {
            // For old format, return empty array as there's no history
            return [];
        }
        // For new format, return the data array
        return contentRecord?.data || [];
    };
    const getLimitBadgeColor = () => {
        if (remainingLaunches > 2) return 'success';
        if (remainingLaunches > 0) return 'warning';
        return 'error';
    };

    const getLimitBadgeIcon = () => {
        if (remainingLaunches > 2) return '🎯';
        if (remainingLaunches > 0) return '⚠️';
        return '🚫';
    };

    const contentMappLimit = () => {
        return (
            <Chip
                icon={<span style={{ fontSize: '16px' }}>{getLimitBadgeIcon()}</span>}
                label={
                    remainingLaunches > 1
                        ? `${remainingLaunches} of ${MAX_LAUNCHES} launches remaining`
                        : remainingLaunches === 1
                            ? `${remainingLaunches} launch remaining`
                            : 'No launches remaining'
                }
                color={getLimitBadgeColor()}
                sx={{
                    fontWeight: 500,
                    fontSize: '13px',
                    height: '32px',
                    marginRight: '12px',
                    '& .MuiChip-icon': {
                        fontSize: '16px',
                        marginLeft: '8px'
                    }
                }}
            />
        );
    }

    return (
        <>
            <div className={classes.containerWrapper}>
                <div className="Heading-checkbox">
                    <div className={classes.dropdownWrapper}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                            <h5>Content Mapping</h5>
                        </div>
                        <div className={classes.categoryWrapper}>
                            <MUISelect
                                fullWidth={false}
                                optionValue={discoveryCtx.state.selectedCategory}
                                label={"Category"}
                                id={filterKey.category}
                                options={(discoveryCtx.category || []).map((category) => {
                                    return {
                                        name: category,
                                        value: category,
                                    };
                                })}
                                onChange={(e) => {
                                    discoveryCtx.updateSelectedCategory(e.target.value);
                                }}
                            />
                            <div className={classes.shareIcon}>
                                <MUIIconButton
                                    title="Click to Share"
                                    onClick={() => setShowShareModal(true)}
                                >
                                    <ShareOutlinedIcon color="primary" />
                                </MUIIconButton>
                            </div>
                        </div>
                    </div>
                    <hr />
                </div>
                <hr />
                <FocusAreas
                    data={focusAreaInputs}
                    setData={setFocusAreaInputs}
                    type={discoveryType === "product" ? ENTITY_LABELS.product : ENTITY_LABELS.brand}
                    isAudit={true}
                    isPropensityToBuy={discoveryCtx.isPropensityToBuy()}
                    mapMetricTitle={discoveryCtx.mapMetricTitle}
                />
                {focusAreaInputs &&
                    <div style={{ marginBottom: "150px" }}>
                        <BuyingJourneyDataTable
                            data={contentMapData?.data}
                            loading={contentMapLoading}
                            filterOptions={contentMapData?.filterKeys}
                            isDisabled={contentMapLoading || remainingLaunches<=0}
                            title={`Content Suggestions`}
                            handleContentMapping={handleContentMapping}
                            selectedContentMapLlmItems={selectedContentMapLlmItems}
                            handleContentMapLlmCheckChange={handleContentMapLlmCheckChange}
                            historyData={getHistoryData()}
                            setFocusAreaInputs={setFocusAreaInputs}
                            setContentMapData={setContentMapData}
                            contentMappLimit={contentMappLimit}
                            pdfExportComponent={
                                contentMapData?.data?.length > 0 ? (
                                    <MetricContentPDFExport {...preparePDFExportProps()} />
                                ) : null
                            }
                        />
                    </div>
                }
            </div>
            {showShareModal &&
                <Modal
                    height="85vh"
                    width="700"
                    title="Share Link"
                    open={showShareModal}
                    handleClose={() => setShowShareModal(false)}
                >
                    <Share setShowShareModal={setShowShareModal} />
                </Modal>
            }
            <ToastContainer />
        </>
    )
}

export default ContentMapping